/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import UAParser from 'ua-parser-js';
import { useTranslation } from 'react-i18next';
import { YMaps, Map, Placemark } from '@pbe/react-yandex-maps';

import legalEntitySignService from 'service/legalEntitySign';
import activityService from 'service/activity';
import currencyService from 'service/currency';
import Button from 'components/base/Button';
import useAuth from 'service/auth/useAuth';
import countryService from 'service/country';
import { authActions } from 'store/auth/auth.slice';
import { shiftIdActions } from 'store/shiftId/shiftId.slice';
import { showAlert } from 'store/alert/alert.thunk';

const SwitchControl = ({ field, label }: { field: any; label: any }) => {
  return (
    <Form.Check
      type="switch"
      label={label}
      className="cursor-pointer"
      checked={field.value}
      onChange={e => {
        field.onChange(e.target.checked);
      }}
    />
  );
};

const tariffOption = [
  {
    label: 'LITE',
    value: 1
  },
  {
    label: 'STANDARD',
    value: 2
  },
  {
    label: 'PREMIUM',
    value: 3
  }
];

const RegisterSecondForm = () => {
  const { t, i18n } = useTranslation();

  const typeOption = [
    {
      value: 'HOLDING',
      label: t('holding')
    },
    {
      value: 'DEALER',
      label: t('dealer')
    },
    {
      value: 'STORE',
      label: t('store')
    },
    {
      value: 'BRAND',
      label: t('brand')
    }
  ];

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({});

  const registerData = useSelector((state: any) => state.register);
  const phoneNumber = useSelector((state: any) => state.auth?.phone_number);
  const dispatch = useDispatch();
  const dispatchAlert: Dispatch<any> = useDispatch();
  const navigate = useNavigate();
  const [uaResult, setUaResult] = useState<any>(null);

  const [activityOption, setActivityOption] = useState<any>([]);
  const [signOption, setSignOption] = useState([]);
  const [currencyOption, setCurrencyOption] = useState<any>([]);
  const [countryOption, setCountryOption] = useState<any>([]);
  const [mapCenter, setMapCenter] = useState([41.2995, 69.2401]); // Default to Tashkent, Uzbekistan
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  const mapStyles = {
    height: '200px',
    width: '100%'
  };

  useEffect(() => {
    const parser = new UAParser();
    const result = parser.getResult();
    setUaResult(result);
  }, []);

  //GET Activity to options
  useQuery(['GET_ACTIVITY_KIND'], async () => {
    await activityService
      .getListForRegister({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.activity_kinds?.map((activity: any) => ({
          value: activity.id,
          pid: activity?.pid || 0,
          label: activity.name?.[i18n?.language]
        }));
        setActivityOption(options);
      });
  });

  //GET Sign to options
  useQuery(['GET_SIGN'], async () => {
    await legalEntitySignService
      .getListForRegister({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.legal_entity_signes?.map((option: any) => ({
          value: option.id,
          label: option.name?.[i18n?.language]
        }));
        setSignOption(options);
      });
  });

  //GET Currency to options
  useQuery(['GET_CURRENCY'], async () => {
    await currencyService
      .getSignCurrency({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.currencies?.map((option: any) => ({
          value: option.id,
          pid: option?.pid || 0,
          label: option.name?.[i18n?.language]
        }));
        setCurrencyOption(options);
      });
  });

  //GET Country to options
  useQuery(['GET_COUNTRY'], async () => {
    await countryService
      .getListForRegister({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.countries?.map((option: any) => ({
          value: option.id,
          pid: option?.pid || 0,
          label: option.name?.[i18n?.language]
        }));
        setCountryOption(options);
      });
  });

  const { register } = useAuth({
    registerProps: {
      onSuccess: (value: any) => {
        const states = {
          token: value.data.access_token,
          user: value.data.user,
          user_permissions: value?.data?.user_permissions,
          legal_entity: value?.data?.legal_entity
        };
        dispatch(authActions.login(states));
        dispatch(
          shiftIdActions.setShiftId(
            value.data.cashbox_id ? value.data.cashbox_id : ''
          )
        );
        dispatch(
          shiftIdActions.setShiftPid(
            value.data.cashbox_pid ? value.data.cashbox_pid : 0
          )
        );
        if (value?.data?.legal_entity?.tariff_pid === 1) {
          navigate('/sign-in');
        } else {
          navigate('/');
        }
      }
    }
  });

  const handleMapClick = (event: any) => {
    const coords = event.get('coords');
    if (coords) {
      setMarkerPosition(coords);
      setMapCenter(coords);
      setValue('l_lat', coords[0].toString(), { shouldValidate: true });
      setValue('l_long', coords[1].toString(), { shouldValidate: true });
    }
  };

  const onSubmit = async (data: any) => {
    setLoading(true);

    let l_long = null;
    let l_lat = null;

    // Check if the data contains a link for `l_long`
    if (data?.l_long) {
      try {
        if (data.l_long.includes('http')) {
          const longUrl = new URL(data.l_long);

          // Parse Yandex Maps link
          if (longUrl.hostname.includes('yandex')) {
            const llParam = longUrl.searchParams.get('ll');
            if (llParam) {
              [l_long, l_lat] = llParam
                .split(',')
                .map(coord => parseFloat(coord));
            }
          }

          // Parse Google Maps link
          if (longUrl.hostname.includes('google')) {
            const coordsMatch = longUrl.pathname.match(/@([\d.-]+),([\d.-]+)/);
            if (coordsMatch) {
              [, l_lat, l_long] = coordsMatch.map(coord => parseFloat(coord));
            }
          }
        } else {
          // If it's not a URL, use the raw value
          l_long = parseFloat(data.l_long);
        }
      } catch (error) {
        console.error('Error parsing map link:', error);
        // Fallback to raw value if parsing fails
        l_long = parseFloat(data.l_long);
      }
    }

    // Check if the data contains a link for `l_lat`
    if (data?.l_lat) {
      try {
        if (data.l_lat.includes('http')) {
          const latUrl = new URL(data.l_lat);

          // Parse Yandex Maps link for latitude
          if (latUrl.hostname.includes('yandex')) {
            const llParam = latUrl.searchParams.get('ll');
            if (llParam) {
              [l_long, l_lat] = llParam
                .split(',')
                .map(coord => parseFloat(coord));
            }
          }

          // Parse Google Maps link for latitude
          if (latUrl.hostname.includes('google')) {
            const coordsMatch = latUrl.pathname.match(/@([\d.-]+),([\d.-]+)/);
            if (coordsMatch) {
              [, l_lat, l_long] = coordsMatch.map(coord => parseFloat(coord));
            }
          }
        } else {
          // If it's not a URL, use the raw value
          l_lat = parseFloat(data.l_lat);
        }
      } catch (error) {
        console.error('Error parsing map link:', error);
        // Fallback to raw value if parsing fails
        l_lat = parseFloat(data.l_lat);
      }
    }

    // If `l_lat` is not available, fallback to `lat` in `data`
    l_lat = l_lat ?? parseFloat(data?.lat);

    // If `l_long` is not available, fallback to `long` in `data`
    l_long = l_long ?? parseFloat(data?.long);

    if (
      l_long === null ||
      l_long === undefined ||
      isNaN(l_long) ||
      l_lat === null ||
      l_lat === undefined ||
      isNaN(l_lat)
    ) {
      setLoading(false);
      /* prettier-ignore */
      dispatchAlert(
        showAlert({
          title: t('error_missing_coordinates'),
          type: 'error'
        })
      );
      return;
    }

    const deviceModel = uaResult?.device?.model
      ? `_${uaResult.device.model}`
      : '';

    const selectedActivity = activityOption.find(
      (option: any) => option.value === data?.activity_id
    );

    const selectedCurrency = currencyOption.find(
      (option: any) => option.value === data?.currency_id
    );

    const selectedCountry = countryOption.find(
      (option: any) => option.value === data?.country_id
    );

    const createdData = {
      legal_entity: {
        activity_kind_id: data?.activity_id,
        activity_kind_pid: selectedActivity?.pid,
        address: data?.address,
        country_id: data?.country_id,
        country_pid: selectedCountry?.pid,
        currency_id: data?.currency_id,
        currency_pid: selectedCurrency?.pid,
        email: data?.email,
        tariff_pid: +data?.tariff_pid,
        is_vat_payer: data?.is_nds,
        legal_entity_sign_id: data?.legal_entity_sign,
        legal_entity_type: data?.legal_entity_type,
        name: data?.name,
        phone_number: `+${phoneNumber}`,
        location: '12.25',
        l_long: l_long ?? data?.l_long,
        l_lat: l_lat ?? data?.l_lat
      },
      user: {
        first_name: registerData?.first_name,
        last_name: registerData?.last_name,
        password: registerData?.password,
        gender: registerData?.gender,
        phone: `+${phoneNumber}`
      },
      user_device: `WEB#${uaResult.browser.name}${deviceModel}`
    };

    try {
      await register.mutateAsync(createdData);
    } catch (error: any) {
      console.log('register next step error', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const countrypid = countryOption?.find(
      (option: any) => option?.value === '9daa2189-99a6-4825-9677-b846731bf70d'
    );
    if (countryOption?.length > 0) {
      setValue('country_id', countrypid?.value);
    }
  }, [setValue, currencyOption]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="text-center mb-7">
        <h3 className="text-1000">{t('registration')}</h3>
        <p className="text-700">{t('to_start_register')}</p>
      </div>

      <div className="react-select-container mb-2">
        <Controller
          name="legal_entity_type"
          control={control}
          defaultValue=""
          rules={{ required: true }}
          render={({ field }) => (
            <Form.Floating>
              <Form.Select {...field} isInvalid={!!errors.legal_entity_type}>
                <option className="d-none" value=""></option>
                {typeOption?.map((option: any) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="option"
                  >
                    {option.label}
                  </option>
                ))}
              </Form.Select>

              <label htmlFor="floatingInputCustom">
                {t('legal_entity_type')}
              </label>
              {errors?.legal_entity_type && (
                <span
                  style={{
                    marginTop: '2px',
                    color: 'red',
                    fontSize: '12px'
                  }}
                >
                  {t('required_field')}
                </span>
              )}
            </Form.Floating>
          )}
        />
      </div>

      <div className="react-select-container mb-2">
        <Controller
          name="legal_entity_sign"
          control={control}
          defaultValue=""
          rules={{ required: true }}
          render={({ field }) => (
            <Form.Floating>
              <Form.Select {...field} isInvalid={!!errors.legal_entity_sign}>
                <option className="d-none" value=""></option>
                {signOption?.map((option: any) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="option"
                  >
                    {option.label}
                  </option>
                ))}
              </Form.Select>

              <label htmlFor="floatingInputCustom">
                {t('sign_legal_Entity')}
              </label>
              {errors?.legal_entity_sign && (
                <span
                  style={{
                    marginTop: '2px',
                    color: 'red',
                    fontSize: '12px'
                  }}
                >
                  {t('required_field')}
                </span>
              )}
            </Form.Floating>
          )}
        />
      </div>

      <div className="react-select-container mb-2">
        <Controller
          name="activity_id"
          control={control}
          defaultValue=""
          rules={{ required: true }}
          render={({ field }) => (
            <Form.Floating>
              <Form.Select {...field} isInvalid={!!errors.activity_id}>
                <option className="d-none" value=""></option>
                {activityOption?.map((option: any) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="option"
                  >
                    {option.label}
                  </option>
                ))}
              </Form.Select>

              <label htmlFor="floatingInputCustom">{t('activity_type')}</label>
              {errors?.activity_id && (
                <span
                  style={{
                    marginTop: '2px',
                    color: 'red',
                    fontSize: '12px'
                  }}
                >
                  {t('required_field')}
                </span>
              )}
            </Form.Floating>
          )}
        />
      </div>

      <div className="react-select-container mb-2">
        <Controller
          name="tariff_pid"
          control={control}
          defaultValue=""
          rules={{ required: true }}
          render={({ field }) => (
            <Form.Floating>
              <Form.Select {...field} isInvalid={!!errors.tariff_pid}>
                <option className="d-none" value=""></option>
                {tariffOption?.map((option: any) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="option"
                  >
                    {option.label}
                  </option>
                ))}
              </Form.Select>

              <label htmlFor="floatingInputCustom">{t('tariff_type')}</label>
              {errors?.tariff_pid && (
                <span
                  style={{
                    marginTop: '2px',
                    color: 'red',
                    fontSize: '12px'
                  }}
                >
                  {t('required_field')}
                </span>
              )}
            </Form.Floating>
          )}
        />
      </div>

      <Controller
        name="name"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <div className="form-icon-container">
            <Form.Floating className="mb-2">
              <Form.Control
                {...field}
                type="text"
                placeholder={t('main_name')}
                onFocus={e => e.target.select()}
                isInvalid={!!errors.name}
              />
              <label htmlFor="firstName">{t('main_name')}</label>
              {errors.name && (
                <Form.Control.Feedback type="invalid">
                  {t('required_field')}
                </Form.Control.Feedback>
              )}
            </Form.Floating>
          </div>
        )}
      />

      <Controller
        name="email"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <div className="form-icon-container">
            <Form.Floating className="mb-2">
              <Form.Control
                {...field}
                type="email"
                placeholder={t('email')}
                onFocus={e => e.target.select()}
                isInvalid={!!errors.email}
              />
              <label htmlFor="firstName">{t('email')}</label>
              {errors.email && (
                <Form.Control.Feedback type="invalid">
                  {t('required_field')}
                </Form.Control.Feedback>
              )}
            </Form.Floating>
          </div>
        )}
      />

      <div className="react-select-container mb-2">
        <Controller
          name="country_id"
          control={control}
          defaultValue=""
          rules={{ required: true }}
          render={({ field }) => (
            <Form.Floating>
              <Form.Select {...field} isInvalid={!!errors.country_id}>
                <option className="d-none" value=""></option>
                {countryOption?.map((option: any) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="option"
                  >
                    {option.label}
                  </option>
                ))}
              </Form.Select>

              <label htmlFor="floatingInputCustom">{t('country')}</label>
              {errors?.country_id && (
                <span
                  style={{
                    marginTop: '2px',
                    color: 'red',
                    fontSize: '12px'
                  }}
                >
                  {t('required_field')}
                </span>
              )}
            </Form.Floating>
          )}
        />
      </div>

      <div className="react-select-container mb-2">
        <Controller
          name="currency_id"
          control={control}
          defaultValue=""
          rules={{ required: true }}
          render={({ field }) => (
            <Form.Floating>
              <Form.Select {...field} isInvalid={!!errors.currency_id}>
                <option className="d-none" value=""></option>
                {currencyOption?.map((option: any) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="option"
                  >
                    {option.label}
                  </option>
                ))}
              </Form.Select>

              <label htmlFor="floatingInputCustom">{t('currency')}</label>
              {errors?.currency_id && (
                <span
                  style={{
                    marginTop: '2px',
                    color: 'red',
                    fontSize: '12px'
                  }}
                >
                  {t('required_field')}
                </span>
              )}
            </Form.Floating>
          )}
        />
      </div>

      <Controller
        name="address"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <div className="form-icon-container">
            <Form.Floating className="mb-2">
              <Form.Control
                {...field}
                type="text"
                placeholder={t('address')}
                onFocus={e => e.target.select()}
                isInvalid={!!errors.address}
              />
              <label htmlFor="firstName">{t('address')}</label>
              {errors.address && (
                <Form.Control.Feedback type="invalid">
                  {t('required_field')}
                </Form.Control.Feedback>
              )}
            </Form.Floating>
          </div>
        )}
      />

      {/* <Row className="mb-3 d-flex gap-2 mb-3">
        <Col>
          <Controller
            name="l_long"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className="form-icon-container">
                <Form.Floating>
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder={t('l_long')}
                    onFocus={e => e.target.select()}
                    isInvalid={!!errors.l_long}
                  />
                  <label htmlFor="firstName">{t('l_long')}</label>
                  {errors.l_long && (
                    <Form.Control.Feedback type="invalid">
                      {t('required_field')}
                    </Form.Control.Feedback>
                  )}
                </Form.Floating>
              </div>
            )}
          />
        </Col>
        <Col>
          <Controller
            name="l_lat"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className="form-icon-container">
                <Form.Floating>
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder={t('l_lat')}
                    onFocus={e => e.target.select()}
                    isInvalid={!!errors.l_lat}
                  />
                  <label htmlFor="firstName">{t('l_lat')}</label>
                  {errors.l_lat && (
                    <Form.Control.Feedback type="invalid">
                      {t('required_field')}
                    </Form.Control.Feedback>
                  )}
                </Form.Floating>
              </div>
            )}
          />
        </Col>
      </Row> */}

      <Row className="mb-3">
        <Col>
          <YMaps
            query={{
              lang: 'ru_RU',
              load: 'package.full'
            }}
          >
            <Map
              defaultState={{ center: mapCenter, zoom: 13 }}
              width={mapStyles.width}
              height={mapStyles.height}
              onClick={handleMapClick}
            >
              {markerPosition && <Placemark geometry={markerPosition} />}
            </Map>
          </YMaps>
        </Col>
      </Row>

      <Form.Group className="d-flex align-items-center mb-3">
        <Controller
          name="is_nds"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <SwitchControl field={field} label={t('vat_payer')} />
          )}
        />
      </Form.Group>

      <Button
        style={{ height: '48px', marginTop: 'auto' }}
        className="w-100 mb-3"
        variant="primary"
        type="submit"
        loading={loading}
        loadingPosition="start"
      >
        {t('registration')}
      </Button>
    </Form>
  );
};

export default RegisterSecondForm;
